import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';

export interface AbaItem {
  readonly id: string;
  readonly rotulo: ReactNode;
  /** Número vira distintivo; `true` vira o ponto. */
  readonly pip?: number | boolean;
}

interface Props {
  itens: readonly AbaItem[];
  valor: string;
  onMuda(id: string): void;
  forma: 'pilula' | 'sublinhada';
  'aria-label'?: string;
  /** id do painel que as abas controlam → aria-controls. */
  controla?: string;
  className?: string;
}

const LIMIAR = 6;

/** Arrasto e roda movem a faixa quando as abas não cabem. */
export function useArrastoX(ref: RefObject<HTMLElement | null>, chave?: unknown) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let pid = -1;
    let x0 = 0;
    let sl0 = 0;
    let moveu = false;

    const down = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      pid = e.pointerId;
      x0 = e.clientX;
      sl0 = el.scrollLeft;
      moveu = false;
    };
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pid) return;
      const dx = e.clientX - x0;
      if (!moveu && Math.abs(dx) < LIMIAR) return;
      if (!moveu) {
        moveu = true;
        try { el.setPointerCapture(pid); } catch { /* soltou no caminho */ }
      }
      el.scrollLeft = sl0 - dx;
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId !== pid) return;
      pid = -1;
      if (!moveu) return;
      el.dataset.arrastou = '1';
      window.setTimeout(() => { delete el.dataset.arrastou; }, 80);
    };
    const click = (e: Event) => {
      if (el.dataset.arrastou !== '1') return;
      e.preventDefault();
      e.stopPropagation();
      delete el.dataset.arrastou;
    };
    const wheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth + 1) return;
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (dx === 0) return;
      el.scrollLeft += dx;
      e.preventDefault();
    };

    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('click', click, true);
    el.addEventListener('wheel', wheel, { passive: false });
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      el.removeEventListener('click', click, true);
      el.removeEventListener('wheel', wheel);
    };
  }, [chave, ref]);
}

export function Abas({ itens, valor, onMuda, forma, 'aria-label': ariaLabel, controla, className }: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const [mais, setMais] = useState(false);
  const [antes, setAntes] = useState(false);
  useArrastoX(listRef, itens.length);

  const medir = () => {
    const el = listRef.current;
    if (!el) return;
    const nextAntes = el.scrollLeft > 2;
    const nextMais = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setAntes(a => (a === nextAntes ? a : nextAntes));
    setMais(m => (m === nextMais ? m : nextMais));
  };

  const trazerAtiva = () => {
    const el = listRef.current;
    if (!el || el.dataset.arrastou === '1') return;
    const ativa = el.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!ativa) return;
    const r = ativa.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    if (r.left < box.left) el.scrollLeft -= box.left - r.left;
    else if (r.right > box.right) el.scrollLeft += r.right - box.right;
  };

  useEffect(() => {
    medir();
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [itens.length]);

  useEffect(() => {
    trazerAtiva();
    medir();
  }, [valor]);

  const ir = (delta: number) => {
    const i = itens.findIndex(a => a.id === valor);
    if (i < 0) return;
    const prox = itens[(i + delta + itens.length) % itens.length];
    onMuda(prox.id);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); ir(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); ir(-1); }
    else if (e.key === 'Home') { e.preventDefault(); onMuda(itens[0].id); }
    else if (e.key === 'End') { e.preventDefault(); onMuda(itens[itens.length - 1].id); }
  };

  if (itens.length === 0) return null;

  return (
    <div
      ref={listRef}
      className={`kit-abas kit-abas--${forma}${mais ? ' kit-abas--mais' : ''}${antes ? ' kit-abas--antes' : ''}${className ? ` ${className}` : ''}`}
      role="tablist"
      aria-label={ariaLabel}
      data-nodrag
      onKeyDown={onKey}
      onScroll={medir}
    >
      {itens.map(item => (
        <button
          key={item.id}
          type="button"
          role="tab"
          className="kit-abas__btn"
          aria-selected={valor === item.id}
          aria-controls={controla}
          tabIndex={valor === item.id ? 0 : -1}
          onClick={() => onMuda(item.id)}
        >
          {item.rotulo}
          {pipDe(item.pip)}
        </button>
      ))}
    </div>
  );
}

function pipDe(pip: number | boolean | undefined) {
  if (pip === true) return <span className="kit-pip kit-pip--dot" aria-hidden="true" />;
  if (typeof pip === 'number' && pip > 0) return <span className="kit-pip">{pip}</span>;
  return null;
}
